import { type Engine, MissingSettingError } from '../types';
import { buildUrl } from '../url';
import { distanceParam, priceParam } from './shared';

const CATEGORIES: Array<[token: string, code: string, label: string]> = [
  ['free', 'zip', 'Free stuff'],
  ['cars', 'cta', 'Cars + trucks'],
  ['moto', 'mca', 'Motorcycles'],
  ['furn', 'fua', 'Furniture'],
  ['elec', 'ela', 'Electronics'],
  ['comp', 'sya', 'Computers'],
  ['appl', 'ppa', 'Appliances'],
  ['tools', 'tla', 'Tools'],
  ['bike', 'bia', 'Bicycles'],
  ['sport', 'sga', 'Sporting goods'],
  ['music', 'msa', 'Musical instruments'],
  ['farm', 'gra', 'Farm + garden'],
  ['heavy', 'hva', 'Heavy equipment'],
  ['garage', 'gms', 'Garage sales'],
  ['apts', 'apa', 'Apartments'],
  ['housing', 'hhh', 'All housing'],
  ['jobs', 'jjj', 'Jobs'],
  ['gigs', 'ggg', 'Gigs'],
  ['svc', 'bbb', 'Services'],
];

// Category codes where items are sold (and so have a seller and delivery).
const FOR_SALE = [
  'zip',
  'cta',
  'mca',
  'fua',
  'ela',
  'sya',
  'ppa',
  'tla',
  'bia',
  'sga',
  'msa',
  'gra',
  'hva',
];
// Categories with a price filter: for sale (minus free stuff) and housing.
const PRICED = [...FOR_SALE.filter(c => c !== 'zip'), 'apa', 'hhh'];

export const craigslist: Engine = {
  id: 'craigslist',
  name: 'Craigslist',
  bang: 'cl',
  aliases: ['craigslist'],
  homepage: 'https://www.craigslist.org',
  category: 'Shopping',
  description:
    'Local classifieds in your region, by category, price and distance.',
  params: [
    {
      id: 'site',
      type: 'text',
      prefix: '@',
      setting: 'craigslistSite',
      pattern: /^[a-z]{2,30}$/,
      label: 'Region',
      group: 'Location',
      placeholder: 'seattle',
      help: 'Craigslist site name. Defaults to the region in Settings.',
    },
    {
      id: 'category',
      type: 'choice',
      label: 'Category',
      group: 'Category',
      help: 'Default is all for-sale listings.',
      options: CATEGORIES.map(([token, code, label]) => ({
        value: code,
        token,
        label,
      })),
    },
    {
      ...priceParam,
      when: { param: 'category', in: PRICED, orUnset: true },
      whenLabel:
        'doesn’t apply to free stuff, garage sales, jobs, gigs or services',
    },
    {
      id: 'hasPic',
      type: 'flag',
      token: 'pic',
      label: 'Has picture',
      group: 'Filters',
    },
    {
      id: 'today',
      type: 'flag',
      token: 'today',
      label: 'Posted today',
      group: 'Filters',
    },
    {
      id: 'titleOnly',
      type: 'flag',
      token: 'title',
      label: 'Search titles only',
      group: 'Filters',
    },
    {
      id: 'dedupe',
      type: 'flag',
      token: 'dedup',
      label: 'Bundle duplicates',
      group: 'Filters',
    },
    {
      id: 'delivery',
      when: { param: 'category', in: FOR_SALE, orUnset: true },
      whenLabel: 'only applies to for-sale categories',
      type: 'flag',
      token: 'deliv',
      label: 'Delivery available',
      group: 'Filters',
    },
    {
      id: 'owner',
      when: {
        param: 'category',
        in: [...FOR_SALE, 'apa', 'hhh'],
        orUnset: true,
      },
      whenLabel: 'only applies to for-sale and housing categories',
      type: 'flag',
      token: 'owner',
      label: 'By owner only',
      group: 'Filters',
    },
    distanceParam('Radius around the zip in Settings.'),
    {
      id: 'sort',
      type: 'choice',
      label: 'Sort',
      group: 'Sort',
      options: [
        { value: 'date', token: 'newest', label: 'Newest' },
        { value: 'priceasc', token: 'low', label: 'Price: low to high' },
        { value: 'pricedsc', token: 'high', label: 'Price: high to low' },
        { value: 'dist', token: 'nearest', label: 'Nearest (needs zip)' },
      ],
    },
  ],
  build({ query, values, settings }) {
    const site =
      typeof values.site === 'string' ? values.site : settings.craigslistSite;
    if (!site) throw new MissingSettingError('craigslistSite', 'Craigslist');
    const needsZip =
      typeof values.distance === 'number' || values.sort === 'dist';
    if (needsZip && !settings.zip)
      throw new MissingSettingError('zip', 'Craigslist');
    const price = typeof values.price === 'object' ? values.price : {};

    return buildUrl(`https://www.craigslist.org/search/area/${site}`, {
      cat: (values.category as string | undefined) ?? 'sss',
      query,
      min_price: price.min,
      max_price: price.max,
      hasPic: values.hasPic === true,
      postedToday: values.today === true,
      srchType: values.titleOnly === true && 'T',
      bundleDuplicates: values.dedupe === true,
      delivery_available: values.delivery === true,
      purveyor: values.owner === true && 'owner',
      postal: needsZip ? settings.zip : undefined,
      radius: values.distance as number | undefined,
      sort: values.sort as string | undefined,
    });
  },
};
